import React, { useEffect, useState } from 'react';
import { Globe2, ArrowRight, Rocket, BookText, FlaskConical, Coins, Inbox, Route, Network, Hourglass } from 'lucide-react';
import {
  XbPage, WithScheme, SchemeHeading, ProvisionalNotice, Card, Fact, C, Copyable, LinkButton, Table,
} from './common';
import { homeLabel, label, schemeBase, countryName, acronymOf, membersOf } from '../../utils/crossBorder';
import { xbApi } from '../../services/xbApi';
import { formatDate } from '../../utils/helpers';

/**
 * The Cross-border corridor's landing page: the scheme chosen in the sidebar,
 * how far its profile can be relied on, the participant's place in it, and the
 * scheme's terms at a glance, with the way to each page.
 */
export default function XbOverview() {
  return (
    <XbPage icon={Globe2} title="Cross-border"
      intro="Regional payment schemes your institution takes part in, each with its own rules, wire format and currencies.">
      <WithScheme>{(scheme) => <Overview scheme={scheme} />}</WithScheme>
      <ComingSoon />
    </XbPage>
  );
}

function Overview({ scheme }) {
  const s = scheme.profile;
  const o = s.offers || {};
  const r = s.replies || {};
  const steps = [
    { to: '/xb/getting-started', icon: Rocket, title: 'Getting started',
      text: `How to reach ${s.id}: endpoints, wire format, signing and replies.` },
    { to: '/xb/profile', icon: BookText, title: 'Scheme profile',
      text: `The ${s.rules} rules the scheme checks, its reason codes and its sources.` },
    { to: '/xb/samples', icon: FlaskConical, title: 'Message samples',
      text: 'Ready-made messages in the scheme\'s format: sign them, send them, read the answers.' },
    ...(o.quotes ? [{ to: '/xb/quotes', icon: Coins, title: 'FX quotes',
      text: s.fx?.quotesLock ? 'Quote a payment and lock its rate.' : 'Ask for an indicative rate.' }] : []),
    { to: '/xb/incoming', icon: Inbox, title: 'Incoming payments',
      text: 'Have the Sandbox pay you, and see whether you answered as the scheme expects.' },
  ];

  return (
    <>
      <SchemeHeading summary={s} />
      <ProvisionalNotice summary={s} />

      <div className="grid gap-6 md:grid-cols-2">
        <Card title="Your place in the scheme" icon={Network}>
          <dl>
            <Fact label="Home country and currency">{homeLabel({ country: scheme.homeCountry, currency: scheme.homeCurrency })}</Fact>
            {s.wire?.memberIdPattern && <Fact label="Member id" mono>{scheme.memberId}</Fact>}
            {s.wire?.hubMemberId && <Fact label="The scheme's own member id" mono>{s.wire.hubMemberId}</Fact>}
            {scheme.assignedAt && <Fact label="Assigned">{formatDate(scheme.assignedAt, 'PP')}</Fact>}
          </dl>
          <p className="text-xs text-gray-500 mt-3">Your base path for this scheme:</p>
          <div className="mt-1"><Copyable value={schemeBase(s.id)} label="the base path" /></div>
        </Card>

        <Card title="How it works" icon={Route}>
          <dl>
            <Fact label="Messages">pacs.008 as <C>{s.messages['pacs.008']}</C></Fact>
            <Fact label="Transport">{s.offers?.polls ? 'You poll an inbox' : 'Callbacks to your endpoints'}</Fact>
            <Fact label="FX">{label('fx', s.fx?.model)}</Fact>
            <Fact label="Liquidity">{label('liquidity', s.liquidity)}</Fact>
            <Fact label="Settlement">{label('settlement', s.settlement?.model)}</Fact>
            <Fact label="Answering a payment">
              <C>{r.incomingReply}</C> {label('level', r.level)}, within {r.responseSeconds} s
              {r.holdMaxSeconds > 0 ? `; a hold may last ${r.holdMaxSeconds} s` : '; no holds'}
            </Fact>
          </dl>
        </Card>
      </div>

      <Corridors corridors={s.corridors} />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {steps.map(({ to, icon: Icon, title, text }) => (
          <div key={to} className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col">
            <p className="font-semibold text-gray-900 flex items-center gap-2">
              <Icon className="w-4 h-4 text-primary-600" aria-hidden="true" /> {title}</p>
            <p className="text-sm text-gray-600 mt-1 flex-1">{text}</p>
            <div className="mt-3"><LinkButton to={to}>Open <ArrowRight className="w-4 h-4" aria-hidden="true" /></LinkButton></div>
          </div>
        ))}
      </div>
    </>
  );
}

/**
 * Regional schemes the Sandbox has no profile for yet: what each is, where it
 * reaches and how it settles. Nothing here can be used yet, so the section says
 * so, and stays out of the way when the list cannot be read.
 */
function ComingSoon() {
  const [schemes, setSchemes] = useState([]);
  useEffect(() => {
    let live = true;
    xbApi.comingSoon().then((list) => { if (live && Array.isArray(list)) setSchemes(list); }).catch(() => {});
    return () => { live = false; };
  }, []);
  if (schemes.length === 0) return null;

  return (
    <Card title="Coming soon" icon={Hourglass}>
      <p className="text-sm text-gray-600 mb-4">
        Regional schemes the Sandbox will add. They cannot be tested here yet; the facts below are public
        descriptions, not a profile of the scheme.
      </p>
      <div className="grid gap-4 md:grid-cols-3">
        {schemes.map((c) => (
          <div key={c.id} className="border border-gray-200 rounded-xl p-4 flex flex-col gap-2">
            <div className="flex items-center justify-between gap-2">
              <p className="font-semibold text-gray-900">{acronymOf(c.name)}</p>
              <span className="text-xs font-medium px-2 py-0.5 rounded bg-gray-100 text-gray-600">Coming soon</span>
            </div>
            <p className="text-xs text-gray-500">{c.name !== acronymOf(c.name) ? `${c.name} · ` : ''}{c.region}</p>
            <p className="text-sm text-gray-700">{c.summary}</p>
            <dl className="text-xs text-gray-600 space-y-1 mt-1">
              <div><dt className="inline font-medium text-gray-700">Run by: </dt><dd className="inline">{c.operator}</dd></div>
              <div><dt className="inline font-medium text-gray-700">Countries: </dt>
                <dd className="inline">{c.countries.map(countryName).join(', ')}</dd></div>
              {c.countriesNote && <div className="text-gray-500">{c.countriesNote}</div>}
              <div><dt className="inline font-medium text-gray-700">Settlement: </dt><dd className="inline">{c.settlement}</dd></div>
            </dl>
          </div>
        ))}
      </div>
    </Card>
  );
}

/**
 * Where payments can go. A regional scheme, where every member pays every
 * other, is shown as its members; anything else as its corridors, one by one.
 */
function Corridors({ corridors }) {
  const { members, complete } = membersOf(corridors);
  if (complete) {
    return (
      <Card title={`Members (${members.length})`}>
        <p className="text-sm text-gray-600 mb-3">Every member can pay every other: the sender in its currency, the receiver paid in its own.</p>
        <Table head={['Country', 'Currency', 'Most per payment']} mono={[1, 2]}
          rows={members.map((m) => [`${countryName(m.country)} (${m.country})`, m.currency,
            m.maxSend ? `${m.maxSend} ${m.currency}` : 'no cap'])} />
      </Card>
    );
  }
  return (
    <Card title="Corridors">
      <Table head={['From', 'To', 'You send', 'Paid out in', 'Most per payment']} mono={[4]}
        rows={(corridors || []).map((c) => [countryName(c.from), countryName(c.to), c.send, c.receive,
          c.maxSend ? `${c.maxSend} ${c.send}` : 'no cap'])} />
    </Card>
  );
}
